/**
 * Spec Parser — products.json içindeki HTML description'larından
 * yapılandırılmış teknik özellik listesi çıkarır.
 */

export interface SpecEntry {
  label: string;
  value: string;
}

/** HTML tag'lerini temizle */
function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/** HTML tablodan spec satırları çıkar */
function parseTable(html: string): SpecEntry[] {
  const rows: SpecEntry[] = [];
  const trRegex = /<tr[^>]*>([\s\S]*?)<\/tr>/gi;
  let trMatch;
  while ((trMatch = trRegex.exec(html)) !== null) {
    const cells: string[] = [];
    const tdRegex = /<t[dh][^>]*>([\s\S]*?)<\/t[dh]>/gi;
    let tdMatch;
    while ((tdMatch = tdRegex.exec(trMatch[1])) !== null) {
      cells.push(stripHtml(tdMatch[1]));
    }
    if (cells.length >= 2 && cells[0] && cells[1]) {
      rows.push({ label: cells[0], value: cells[1] });
    }
  }
  return rows;
}

/** Satır bazlı (label: value) pattern'lerini parse et */
function parseColonPattern(text: string): SpecEntry[] {
  const entries: SpecEntry[] = [];
  const lines = text.split(/\n|<br\s*\/?>/i);
  for (const line of lines) {
    const clean = stripHtml(line).trim();
    if (!clean) continue;
    const colonIdx = clean.indexOf(':');
    if (colonIdx > 0 && colonIdx < clean.length - 1) {
      const label = clean.slice(0, colonIdx).trim();
      const value = clean.slice(colonIdx + 1).trim();
      if (label.length < 60 && value.length > 0) {
        entries.push({ label, value });
      }
    }
  }
  return entries;
}

/** Li elementlerinden özellik listesi çıkar */
function parseLiList(html: string): SpecEntry[] {
  const entries: SpecEntry[] = [];
  const liRegex = /<li[^>]*>([\s\S]*?)<\/li>/gi;
  let m;
  while ((m = liRegex.exec(html)) !== null) {
    const clean = stripHtml(m[1]).trim();
    if (!clean) continue;
    const colonIdx = clean.indexOf(':');
    if (colonIdx > 0 && colonIdx < clean.length - 1) {
      entries.push({
        label: clean.slice(0, colonIdx).trim(),
        value: clean.slice(colonIdx + 1).trim(),
      });
    }
  }
  return entries;
}

/** SKU / ürün kodu çıkar */
export function extractSku(text: string): string | undefined {
  // Pattern: #XX.XXXXX veya DS.XXXXX, CR.XXXXX vb.
  const m = text.match(/\b([A-Z]{1,3}[\.\-]\w{3,12})\b/);
  return m ? m[1] : undefined;
}

/** Ana parser fonksiyonu */
export function parseSpecs(description: string): SpecEntry[] {
  if (!description) return [];

  const specs: SpecEntry[] = [];
  const seen = new Set<string>();

  // 1. HTML tablolardan çıkar
  specs.push(...parseTable(description));

  // 2. Li listelerinden çıkar
  specs.push(...parseLiList(description));

  // 3. Kolon pattern'i
  specs.push(...parseColonPattern(description));

  // Tekrarları temizle
  return specs.filter(s => {
    const key = s.label.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return s.label.length > 0 && s.value.length > 0;
  });
}

/** Description'dan feature taglerini çıkar */
export function parseFeatures(description: string): string[] {
  if (!description) return [];
  const features: string[] = [];
  const strongRegex = /<strong[^>]*>([\s\S]*?)<\/strong>/gi;
  let m;
  while ((m = strongRegex.exec(description)) !== null) {
    const clean = stripHtml(m[1]).trim();
    if (clean && clean.length < 50 && !clean.includes(':')) {
      features.push(clean);
    }
  }
  return [...new Set(features)].slice(0, 8);
}

/** Düz metin açıklama çıkar (HTML olmadan) */
export function parseDescription(description: string): string {
  if (!description) return '';
  // Tabloları ve listleri kaldır, düz p/div içeriğini al
  const withoutTable = description.replace(/<table[\s\S]*?<\/table>/gi, '');
  const withoutList = withoutTable.replace(/<[uo]l[\s\S]*?<\/[uo]l>/gi, '');
  return stripHtml(withoutList).replace(/\s+/g, ' ').trim();
}
